import { CardNumberElement, useElements, useStripe } from "@stripe/react-stripe-js";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import * as Fonts from "../../../../components/Fonts";
import { LoadingModal } from "../../../../components/LoadingModal";
import { SubHeader } from "../../../../components/SubHeader";
import constants from "../../../../constants";
import { api } from "../../../../services/api";
import { formatDate as formatLocaleDate } from "../../../../services/i18ln/format";
import { YEARLY_SAVING } from "../../pricing";
import { PaymentMethod, PaymentMethods, Plan } from "../Checkout/PaymentMethods";
import { CardErrors, EMPTY_CARD, StripeProvider, validateCard } from "../Checkout/StripeCard";
import { Wallet, WalletAvailability, WalletProbe, WalletSetupButton } from "../Checkout/WalletButton";
import { PlanCard, Plans } from "../Features/styles";
import { Actions, ChangeSummary, Container, Section } from "./styles";
import { useManagedSubscription, useSave } from "./useManagedSubscription";

/**
 * A PLUS paid once with Pix or boleto moves to a card that renews by itself (monthly or yearly). Nothing is
 * charged now: the paid period stays, and the card is charged when it ends.
 */
export const ToCardPage = () => (
  <StripeProvider>
    <ToCardContent />
  </StripeProvider>
);

const ToCardContent = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const stripe = useStripe();
  const elements = useElements();
  const prefix = "pages.subscription.manage.toCard";
  const { detail, isLoading, detailUrl, siteId } = useManagedSubscription();
  const { state, error, save } = useSave(detailUrl, navigate);

  const [plan, setPlan] = useState<Plan>("ANNUALLY");
  const [method, setMethod] = useState<PaymentMethod>();
  const [card, setCard] = useState(EMPTY_CARD);
  const [cardErrors, setCardErrors] = useState<CardErrors>({});
  const [availableWallets, setAvailableWallets] = useState<WalletAvailability>({ applePay: false, googlePay: false });
  const [stripeError, setStripeError] = useState<string>();

  // Only a PLUS paid once has this to do (the others manage their card on its own screen)
  useEffect(() => {
    if (!isLoading && !detail?.paymentType) {
      navigate(detailUrl, { replace: true });
    }
  }, [detail?.paymentType, detailUrl, isLoading, navigate]);

  if (isLoading || !detail?.paymentType) {
    return <LoadingModal />;
  }

  const periodEnd = dayjs(detail.currentPeriodEnd);
  // Past its end (the tolerance after it): the first charge is now
  const startsNow = !periodEnd.isAfter(dayjs().add(1, "hour"));
  const startDate = formatLocaleDate(periodEnd, i18n.language);
  const isAnnual = plan === "ANNUALLY";
  const price = isAnnual ? constants.price.year.value : constants.price.month.value;
  const planTitle = (value: Plan) =>
    t(`pages.subscription.features.plans.${value === "ANNUALLY" ? "annually" : "monthly"}.title`);
  const isWallet = method === "applePay" || method === "googlePay";

  const switchTo = (paymentMethodId: string) => api.switchToCard(siteId, { plan, paymentMethodId });

  // The card is saved on Stripe (with the bank's 3D Secure when it asks), then becomes the subscription's
  const saveCard = () => {
    const errors = validateCard(card);
    setCardErrors(errors);
    const cardElement = elements?.getElement(CardNumberElement);
    if (Object.keys(errors).length || !stripe || !cardElement) {
      return;
    }
    setStripeError(undefined);
    save(async () => {
      const { clientSecret } = await api.createCardSetup(siteId);
      const { setupIntent, error: setupError } = await stripe.confirmCardSetup(clientSecret, {
        payment_method: { card: cardElement, billing_details: { name: card.name.trim() } },
      });
      if (setupError || !setupIntent?.payment_method) {
        // Stripe's message, already in the person's language
        setStripeError(setupError?.message);
        throw setupError;
      }
      await switchTo(setupIntent.payment_method as string);
    });
  };

  const walletFields = (wallet: Wallet) => (
    <WalletSetupButton
      wallet={wallet}
      createSetup={() => api.createCardSetup(siteId)}
      onStart={() => setStripeError(undefined)}
      onCancel={() => undefined}
      onSaved={paymentMethodId => save(() => switchTo(paymentMethodId))}
      onError={message => setStripeError(message || t("pages.subscription.manage.errors.generic"))}
    />
  );

  return (
    <Container>
      <SubHeader
        title={t(`${prefix}.title`)}
        crumbs={[
          { label: t("pages.plusBenefits.title"), to: "/keepo/plus" },
          { label: t("pages.subscriptionDetail.title"), to: detailUrl },
        ]}
      />

      <Section>
        <Fonts.Label>{t("pages.subscription.manage.changePlan.choose")}</Fonts.Label>
        <Plans
          role="radiogroup"
          aria-label={t("pages.subscription.manage.changePlan.choose")}
          style={{ maxWidth: "none", marginTop: 0 }}>
          <PlanCard
            type="button"
            role="radio"
            aria-checked={isAnnual}
            $selected={isAnnual}
            onClick={() => setPlan("ANNUALLY")}>
            <span className="save">{t("pages.subscription.features.page.plans.save", { value: YEARLY_SAVING })}</span>
            <span className="radio" aria-hidden="true" />
            <span className="name">
              <b>
                {planTitle("ANNUALLY")}
                <span className="discount">{constants.price.year.discount}</span>
              </b>
              <span>{t("pages.subscription.features.page.plans.yearly", { value: constants.price.year.value })}</span>
            </span>
            <span className="value">
              {constants.price.year.perMonth}
              <small>{t("pages.subscription.features.plans.perMonth")}</small>
            </span>
          </PlanCard>
          <PlanCard
            type="button"
            role="radio"
            aria-checked={!isAnnual}
            $selected={!isAnnual}
            onClick={() => setPlan("MONTHLY")}>
            <span className="radio" aria-hidden="true" />
            <span className="name">
              <b>{planTitle("MONTHLY")}</b>
              <span>{t("pages.subscription.features.page.plans.monthly")}</span>
            </span>
            <span className="value">
              {constants.price.month.value}
              <small>{t("pages.subscription.features.plans.perMonth")}</small>
            </span>
          </PlanCard>
        </Plans>
      </Section>

      <Section>
        <Fonts.Label>{t("pages.subscription.manage.changePlan.whatChanges")}</Fonts.Label>
        <ChangeSummary>
          <div>
            <dt>{startsNow ? t(`${prefix}.summary.now`) : t(`${prefix}.summary.from`, { date: startDate })}</dt>
            <dd>{price}</dd>
          </div>
          <div className="total">
            <dt>{t(`${prefix}.summary.today`)}</dt>
            <dd>{startsNow ? price : "R$ 0,00"}</dd>
          </div>
          <span className="note">{t(`${prefix}.${startsNow ? "noteNow" : "note"}`)}</span>
        </ChangeSummary>
      </Section>

      <Section>
        <Fonts.Label>{t("pages.subscription.manage.paymentMethod.new")}</Fonts.Label>
        <WalletProbe onAvailable={setAvailableWallets} />
        {/* Only the card and the wallets: Pix and boleto are what this is moving away from */}
        <PaymentMethods
          plan={plan}
          isBrazil={false}
          availableWallets={availableWallets}
          method={method}
          onMethodChange={next => {
            setMethod(next);
            setStripeError(undefined);
          }}
          card={card}
          onCardChange={next => {
            setCard(next);
            if (Object.keys(cardErrors).length) {
              setCardErrors(validateCard(next));
            }
          }}
          cardErrors={cardErrors}
          disabled={state === "saving"}
          onSwitchToAnnual={() => setPlan("ANNUALLY")}
          walletFields={walletFields}
        />
      </Section>

      <Actions>
        {(stripeError || error) && (
          <p className="error" role="alert">
            {stripeError || t(`pages.subscription.manage.errors.${error}`)}
          </p>
        )}
        {state === "done" ? (
          <p className="done">{startsNow ? t(`${prefix}.savedNow`) : t(`${prefix}.saved`, { date: startDate })}</p>
        ) : (
          // The wallets save with their own button
          !isWallet && (
            <button
              type="button"
              className="primary"
              disabled={method !== "card" || state === "saving"}
              aria-busy={state === "saving"}
              onClick={saveCard}>
              {t(`${prefix}.save`)}
            </button>
          )
        )}
        <button type="button" className="secondary" onClick={() => navigate(detailUrl)}>
          {t("pages.subscription.manage.cancel")}
        </button>
      </Actions>
    </Container>
  );
};
